import { useEffect, useRef, useState, type ReactElement, type RefObject } from "react";
import { SocialPostSceneRenderer } from "@runcheetah/ui";
import type { SocialPostScenePlanV1 } from "@runcheetah/shared";
import type { Stage as KonvaStage } from "konva/lib/Stage";

import {
  CampaignCreativeScenePreview,
  type CampaignCreativeBrandkit,
  type CampaignCreativeLinkedAsset,
} from "@/components/campaign/campaign-creative-scene-preview";
import { InstagramPostChrome } from "@/components/campaign/instagram-post-chrome";
import { resolveSocialPostPreviewScale } from "@/lib/social-post-preview";
import {
  resolveSocialPostChromeFrame,
  resolveSocialPostChromeVariant,
} from "@/lib/social-post-chrome";
import { cn } from "@/lib/utils";

export type SocialPostPreviewProps = {
  platform: SocialPostScenePlanV1["platform"];
  type: SocialPostScenePlanV1["postType"];
  renderMode?: "editor" | "preview" | "canvas" | "dom";
  plan: SocialPostScenePlanV1;
  linkedAssets: CampaignCreativeLinkedAsset[];
  brandkit: CampaignCreativeBrandkit | null;
  profileLabel?: string;
  caption?: string;
  hashtags?: string[];
  maxWidth?: number;
  fillContainer?: "cover" | "contain";
  className?: string;
  headerAction?: ReactElement;
  mediaOverlay?: ReactElement;
  showMetadata?: boolean;
  sceneStageRef?: RefObject<KonvaStage | null>;
};

export function SocialPostPreview({
  platform,
  type,
  renderMode = "editor",
  plan,
  linkedAssets,
  brandkit,
  profileLabel = "brand",
  caption = "",
  hashtags = [],
  maxWidth,
  fillContainer,
  className,
  headerAction,
  mediaOverlay,
  showMetadata = true,
  sceneStageRef,
}: SocialPostPreviewProps) {
  if (renderMode === "editor" || renderMode === "canvas") {
    return (
      <CampaignCreativeScenePreview
        plan={plan}
        linkedAssets={linkedAssets}
        brandkit={brandkit}
        profileLabel={profileLabel}
        caption={caption}
        hashtags={hashtags}
        maxWidth={maxWidth}
      />
    );
  }

  return (
    <SocialPostRenderedPreview
      platform={platform}
      type={type}
      plan={plan}
      linkedAssets={linkedAssets}
      brandkit={brandkit}
      profileLabel={profileLabel}
      caption={caption}
      hashtags={hashtags}
      maxWidth={maxWidth}
      fillContainer={fillContainer}
      className={className}
      headerAction={headerAction}
      mediaOverlay={mediaOverlay}
      showMetadata={showMetadata}
      sceneStageRef={sceneStageRef}
    />
  );
}

function SocialPostRenderedPreview({
  platform,
  type,
  plan,
  linkedAssets,
  brandkit,
  profileLabel,
  caption,
  hashtags,
  maxWidth = 360,
  fillContainer,
  className,
  headerAction,
  mediaOverlay,
  showMetadata = true,
  sceneStageRef,
}: Required<Pick<SocialPostPreviewProps, "platform" | "type" | "plan" | "linkedAssets">> &
  Pick<
    SocialPostPreviewProps,
    | "brandkit"
    | "profileLabel"
    | "caption"
    | "hashtags"
    | "maxWidth"
    | "fillContainer"
    | "className"
    | "headerAction"
    | "mediaOverlay"
    | "showMetadata"
    | "sceneStageRef"
  >) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const mediaWidth = plan.scene.canvas.width;
  const mediaHeight = plan.scene.canvas.height;
  const variant = resolveSocialPostChromeVariant(platform, type);

  const sceneCanvas = (
    <SocialPostSceneRenderer
      plan={plan}
      linkedAssets={linkedAssets}
      brandkit={brandkit ?? null}
      stageRef={sceneStageRef}
      className="block overflow-hidden"
    />
  );

  useEffect(() => {
    if (showMetadata) {
      const element = containerRef.current;
      if (!element) return;
      const update = () => {
        setContainerWidth(Math.max(0, Math.floor(element.clientWidth)));
      };
      update();
      const observer = new ResizeObserver(update);
      observer.observe(element);
      return () => observer.disconnect();
    }
  }, [maxWidth, showMetadata]);

  if (!showMetadata) {
    return (
      <SceneOnlyPreview
        mediaWidth={mediaWidth}
        mediaHeight={mediaHeight}
        fill={fillContainer}
        className={className}
      >
        {sceneCanvas}
      </SceneOnlyPreview>
    );
  }

  const frame = resolveSocialPostChromeFrame({ variant, mediaWidth, mediaHeight });
  const availableWidth =
    containerWidth > 0
      ? Math.min(containerWidth, maxWidth ?? frame.width)
      : (maxWidth ?? frame.width);
  const preview = resolveSocialPostPreviewScale({
    frameWidth: frame.width,
    frameHeight: frame.height,
    maxWidth: availableWidth,
  });

  return (
    <div
      ref={containerRef}
      className={cn("w-full", className)}
      style={{ maxWidth: maxWidth ?? frame.width, height: preview.height }}
    >
      <div
        style={{
          width: frame.width,
          height: frame.height,
          transform: `scale(${preview.scale})`,
          transformOrigin: "top left",
        }}
      >
        <InstagramPostChrome
          platform={platform}
          postType={type}
          profileLabel={profileLabel ?? "brand"}
          caption={caption ?? ""}
          hashtags={hashtags ?? []}
          headerAction={headerAction}
          mediaOverlay={mediaOverlay}
          media={sceneCanvas}
        />
      </div>
    </div>
  );
}

function SceneOnlyPreview({
  mediaWidth,
  mediaHeight,
  fill,
  className,
  children,
}: {
  mediaWidth: number;
  mediaHeight: number;
  /** Fill parent container — "cover" crops to fill, "contain" letterboxes. Parent must have explicit size. */
  fill?: "cover" | "contain";
  className?: string;
  children: ReactElement;
}) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const update = () => {
      if (fill) {
        const scaleByWidth = outer.clientWidth / mediaWidth;
        const scaleByHeight = outer.clientHeight / mediaHeight;
        // cover = max, contain = min
        const scale =
          fill === "cover"
            ? Math.max(scaleByWidth, scaleByHeight)
            : Math.min(scaleByWidth, scaleByHeight);
        const scaledWidth = mediaWidth * scale;
        const scaledHeight = mediaHeight * scale;
        inner.style.left = `${(outer.clientWidth - scaledWidth) / 2}px`;
        inner.style.top = `${(outer.clientHeight - scaledHeight) / 2}px`;
        inner.style.transform = `scale(${scale})`;
      } else {
        inner.style.left = "0px";
        inner.style.top = "0px";
        inner.style.transform = `scale(${outer.clientWidth / mediaWidth})`;
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    return () => ro.disconnect();
  }, [mediaWidth, mediaHeight, fill]);

  return (
    <div
      ref={outerRef}
      className={cn("relative overflow-hidden", fill ? "h-full w-full" : "w-full", className)}
      style={fill ? undefined : { aspectRatio: `${mediaWidth} / ${mediaHeight}` }}
    >
      <div
        ref={innerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: mediaWidth,
          height: mediaHeight,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
}
